import React, { useCallback, useEffect, useRef, useState } from "react";

import { Text, TextEditor } from "@performyard/py-components";
import type { ICellRendererParams, TextEditorRef } from "@performyard/py-components";

import type { DecisionRow } from "../../types";
import { DecisionTableContext } from "../MyDecisions/types";

import { NOTES_MAX_LENGTH } from "./utils";

export const SuperRowRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	const notesValuesRef = context?.notesValuesRef;
	const activeNoteEditorRef = context?.activeNoteEditorRef;
	/** Set on super rows to `employee_id` so it matches `notesByDecisionRowId` */
	const noteRowId = row?.parentRowId ?? "";
	const serverNotesText = row?.notes != null ? String(row.notes) : "";
	const hasServerNotes = serverNotesText.length > 0;
	const onNotesChange = context?.onNotesChange;
	const editorRef = useRef<TextEditorRef>(null);

	const [notesDraft, setNotesDraft] = useState(() => {
		if (noteRowId && notesValuesRef && noteRowId in notesValuesRef.current) {
			return notesValuesRef.current[noteRowId];
		}
		return serverNotesText;
	});

	// Row data from the grid is server-sourced; drafts live in `notesValuesRef`. After AG Grid
	// `refreshCells({ force: true })` (e.g. when increase fields change elsewhere), this cell remounts
	// and must rehydrate from the ref or autosave appears broken for non-first groups.
	useEffect(() => {
		if (!noteRowId || !notesValuesRef) {
			setNotesDraft(serverNotesText);
			return;
		}
		if (noteRowId in notesValuesRef.current) {
			setNotesDraft(notesValuesRef.current[noteRowId]);
			return;
		}
		setNotesDraft(serverNotesText);
	}, [serverNotesText, noteRowId, notesValuesRef]);

	// Restore focus when this component remounts while the user was actively editing.
	// When AG Grid refreshes the full-width row (e.g. after autosave triggers a re-render),
	// the component unmounts and remounts, causing the TextEditor to lose focus.
	useEffect(() => {
		if (activeNoteEditorRef?.current === noteRowId && noteRowId) {
			// Defer focus to next frame so the TipTap editor is fully initialized
			const raf = requestAnimationFrame(() => {
				editorRef.current?.focus();
			});
			return () => cancelAnimationFrame(raf);
		}
	}, [activeNoteEditorRef, noteRowId]);

	const handleNotesEditorChange = useCallback(
		(value: string) => {
			const val = value.slice(0, NOTES_MAX_LENGTH);
			setNotesDraft(val);
			if (activeNoteEditorRef && noteRowId) {
				activeNoteEditorRef.current = noteRowId;
			}
			if (noteRowId) onNotesChange?.(noteRowId, val);
		},
		[noteRowId, onNotesChange, activeNoteEditorRef]
	);

	const handleEditorBlur = useCallback(
		(event: FocusEvent) => {
			if (!activeNoteEditorRef || activeNoteEditorRef.current !== noteRowId) return;
			if (event.relatedTarget != null) {
				// Focus moved to a real element — user intentionally left the editor
				activeNoteEditorRef.current = null;
			} else {
				// relatedTarget is null: either AG Grid is remounting the row (focus will be
				// restored on remount) or the user clicked non-focusable space. Use a short
				// delay to let the remount happen; if it doesn't, clear the ref.
				setTimeout(() => {
					if (activeNoteEditorRef.current === noteRowId) {
						activeNoteEditorRef.current = null;
					}
				}, 100);
			}
		},
		[activeNoteEditorRef, noteRowId]
	);

	if (!row || !context) return null;
	return (
		<div
			className="flex flex-col gap-2 px-6"
			style={{ padding: "16px 24px" }}
			onKeyDown={(e) => e.stopPropagation()}
			onKeyUp={(e) => e.stopPropagation()}
		>
			<Text size="paragraph-3" weight="semi-bold">
				Notes
			</Text>
			<div className="flex flex-col gap-1">
				<TextEditor
					ref={editorRef}
					key={noteRowId}
					id={`${noteRowId}_notes`}
					onChange={handleNotesEditorChange}
					onBlur={handleEditorBlur}
					initialValue={notesDraft}
					placeholder={!hasServerNotes ? `Add notes about ${row.employee_name}` : undefined}
					characterLimit={NOTES_MAX_LENGTH}
					minHeight={140}
					readOnly={row.status !== "pending"}
					dangerouslySetClassName="h-[120px] w-full max-w-[1216px] resize-none rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-5 focus:outline-none"
				/>
			</div>
		</div>
	);
};
